import { HttpClient } from '@angular/common/http';
import { ApiConfig } from '@kildenconfig/api-config';
import { AppConfig } from '@kildenconfig/app.config';
import { LayerStylesConst } from '@kildenshared/constants/styles/layer-styles.const';
import { LayerConfigInterface } from '@kildenshared/interfaces/layer-config.interface';
import { LayerConfigsType } from '@kildenshared/interfaces/layer-configs.type';
import { Map as OlMap } from 'ol';
import Feature from 'ol/Feature';
import GeoJSON, { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';
import { Geometry } from 'ol/geom';
import ImageLayer from 'ol/layer/Image';
import { Options as VectorLayerOptions } from 'ol/layer/BaseVector';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { bbox } from 'ol/loadingstrategy';
import ImageWMS, { Options as ImageWMSOptions } from 'ol/source/ImageWMS';
import VectorSource, { Options as VectorSourceOptions } from 'ol/source/Vector';
import { take, tap } from 'rxjs';
import Swal from 'sweetalert2';
import { createXYZ } from 'ol/tilegrid';
import TileWMS, { Options as TileWMSOptions } from 'ol/source/TileWMS';
import { LayerHelper } from '@kildencore/helpers/layer.helper';
import { TicketService } from '@kildencore/services/ticket.service';

type ThemeWmsLayer = TileLayer<TileWMS> | ImageLayer<ImageWMS>;
type ThemeLayer = VectorLayer<VectorSource> | ThemeWmsLayer;

// Maximum dimension for ImageWMS requests.
const MAX_IMAGE_WMS_DIMENSION = 4096;

export class ThemeLayers {
  themeLayersList: ThemeLayer[] = [];
  private _httpClient!: HttpClient;
  private _ticketService?: TicketService;
  // Internal list of failing layers in this collection
  private _layersWithErrors: string[] = [];
  private _map: OlMap | undefined;
  private _sourceUrlExceptionMap = new Map<string, string | undefined>([
    [AppConfig.IDBOD_FOREST_ROADS_WFS, undefined],
    [AppConfig.IDBOD_NIB_2, undefined],
    [AppConfig.IDBOD_NIB_SATELLITE, undefined],
    [AppConfig.WILDLIFE_POINTSLAYER_ID, undefined],
    [AppConfig.WILDLIFE_TRACKSLAYER_ID, undefined],
  ]);

  constructor(options: {
    layerConfigs: LayerConfigsType;
    map?: OlMap;
    httpClient?: HttpClient;
    ticketService?: TicketService;
    errorService?: any;
  }) {
    if (options.httpClient) {
      this._httpClient = options.httpClient;
    }
    if (options.ticketService) {
      this._ticketService = options.ticketService;
    }
    if (options.map) {
      this._map = options.map;
    }

    for (const l in options.layerConfigs) {
      if (
        (options.layerConfigs[l].type === 'wms' || options.layerConfigs[l].type === 'wms_text') &&
        !options.layerConfigs[l].background &&
        options.layerConfigs[l].singleTile
      ) {
        // console.log('layer[%s]:', l, layers[l].singleTile);
        const newLayer = this._createWMSLayer(l, options.layerConfigs[l]);
        this.themeLayersList.push(newLayer);
      } else if (
        (options.layerConfigs[l].type === 'api' || options.layerConfigs[l].type === 'wfs') &&
        !options.layerConfigs[l].background
      ) {
        const newLayer = this._createWFSLayer(l, options.layerConfigs[l]);
        // console.log(`newLayer`, l, newLayer.getSource()?.getFeatures().length);
        this.themeLayersList.push(newLayer);
      }
    }
  }

  addLayer<T extends ThemeLayer>(layer: T): T {
    this.themeLayersList.push(layer);
    return this.themeLayersList[this.themeLayersList.length - 1] as T;
  }

  /**
   * Remove the id from internal layer list.
   * @param  id               Layer id
   * @return    void
   */
  clearFromErrorList(id: string) {
    this._removeErrorLayer(id);
  }

  /**
   * Find and return the layer with id
   * @param  id               Id of the layer|
   * @return    Openlayers layer if id is found, null otherwise
   */
  find(id: string): ThemeLayer | null {
    for (let l = 0; l < this.themeLayersList.length; l++) {
      if (this.themeLayersList[l].get('id') === id) {
        return this.themeLayersList[l];
      }
    }
    return null;
  }

  setMap(map: OlMap | undefined): void {
    this._map = map;
  }

  private _addErrorLayer(id: string) {
    if (this._layersWithErrors.indexOf(id, 0) < 0) {
      this._layersWithErrors.push(id);
      return true;
    }
    return false;
  }
  /**
   * Extracts custom query parameters from the config object.
   * The parameters are expected to be in the format "key1=value1&key2=value2".'
   * It comes from backend and is set in colonne custom_query_parameter in db.
   */
  private extractCustomQueryParams(config: { customQueryParameter?: string }): Record<string, string> {
    const raw = config.customQueryParameter;
    if (!raw) return {};

    return Object.fromEntries(
      raw.split('&').map(part => {
        const [key, value] = part.split('=');
        return [decodeURIComponent(key), decodeURIComponent(value)];
      })
    );
  }

  private _createWMSLayer(idBod: string, config: Partial<LayerConfigInterface>): ThemeWmsLayer {
    if (config.type === 'wms_text') {
      return this._createImageWMSLayer(idBod, config);
    }

    return this._createTileWMSLayer(idBod, config);
  }

  private _handleWmsSourceError(config: Partial<LayerConfigInterface>) {
    if (config.idBod === 'norgeibilder_2') {
      return;
    }

    if (this._addErrorLayer(config.label as string)) {
      Swal.fire({
        title: ApiConfig.defaultErrorHeader,
        html: 'Henting av temakartet <b>' + this._getFailingLayersText() + '</b> feilet: <BR> Prøv igjen senere!',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    }
  }

  private _registerImageWmsSourceEvents(source: ImageWMS) {
    const instanceThis = this;

    source.on('imageloadend', function (e) {
      const config = e.target.get('config');
      instanceThis._removeErrorLayer(config.label);
    });
    source.on('imageloaderror', function (e) {
      const config = e.target.get('config');
      instanceThis._handleWmsSourceError(config);
    });
  }

  private _capImageWmsUrl(url: string): string {
    try {
      const parsedUrl = new URL(url);
      const width = Number(parsedUrl.searchParams.get('WIDTH'));
      const height = Number(parsedUrl.searchParams.get('HEIGHT'));

      if (!Number.isNaN(width) && width > MAX_IMAGE_WMS_DIMENSION) {
        parsedUrl.searchParams.set('WIDTH', MAX_IMAGE_WMS_DIMENSION.toString());
      }

      if (!Number.isNaN(height) && height > MAX_IMAGE_WMS_DIMENSION) {
        parsedUrl.searchParams.set('HEIGHT', MAX_IMAGE_WMS_DIMENSION.toString());
      }

      return parsedUrl.toString();
    } catch {
      return url;
    }
  }

  private _registerTileWmsSourceEvents(source: TileWMS) {
    const instanceThis = this;

    source.on('tileloadend', function (e) {
      const config = e.target.get('config');
      instanceThis._removeErrorLayer(config.label);
    });
    source.on('tileloaderror', function (e) {
      const config = e.target.get('config');
      instanceThis._handleWmsSourceError(config);
      // Early return for ortho layers. During startup token can be temporarily missing,
      // which gives expected transient WMS errors we should not show to user.
      if (config.idBod === AppConfig.IDBOD_NIB_2 || config.idBod === AppConfig.IDBOD_NIB_SATELLITE) {
        return;
      }
    });
  }

  private _createTileWMSLayer(idBod: string, config: Partial<LayerConfigInterface>): TileLayer<TileWMS> {
    let styles = '';
    if (config.wmsUrl?.toLowerCase().includes('styles=')) {
      styles = '' + new URL(config.wmsUrl.toLowerCase()).searchParams.get('styles');
    }
    const customParams = this.extractCustomQueryParams(config);
    const nibToken =
      (idBod === AppConfig.IDBOD_NIB_2 || idBod === AppConfig.IDBOD_NIB_SATELLITE) && this._ticketService
        ? this._ticketService.getNibToken()
        : undefined;

    const sourceConfig: TileWMSOptions = {
      url: this._getSourceUrl(idBod, config),
      attributions: config.attribution,
      params: {
        FORMAT: 'image/' + config.format,
        LAYERS: config.wmsLayers,
        VERSION: config.wms_version,
        STYLES: styles,
        ...(nibToken ? { token: nibToken } : {}),
        ...customParams,
      },
      tileGrid: createXYZ({ tileSize: 512 }), // færre tiles = færre requests
      // Avoid cross-fade/interim tile blending on ortho variant changes.
      transition: idBod === AppConfig.IDBOD_NIB_2 || idBod === AppConfig.IDBOD_NIB_SATELLITE ? 0 : undefined,
    };

    const newLayerSource = new TileWMS(sourceConfig);
    newLayerSource.set('config', config);
    newLayerSource.set('customPrintSerializerParams', {
      ...customParams,
    });
    this._registerTileWmsSourceEvents(newLayerSource);

    const layerConfig = {
      properties: { id: idBod },
      source: newLayerSource,
      opacity: config.opacity || AppConfig.DEFAULT_LAYER_OPACITY,
      visible: false,
      zIndex: AppConfig.ZINDEX_THEMELAYERS,
      maxResolution: config.maxResolution,
      minResolution: config.minResolution,
    };
    return new TileLayer(layerConfig);
  }

  private _createImageWMSLayer(idBod: string, config: Partial<LayerConfigInterface>): ImageLayer<ImageWMS> {
    let styles = '';
    if (config.wmsUrl?.toLowerCase().includes('styles=')) {
      styles = '' + new URL(config.wmsUrl.toLowerCase()).searchParams.get('styles');
    }
    const customParams = this.extractCustomQueryParams(config);

    const sourceConfig: ImageWMSOptions = {
      url: this._getSourceUrl(idBod, config),
      attributions: config.attribution,
      hidpi: false,
      ratio: 1,
      params: {
        FORMAT: 'image/' + config.format,
        LAYERS: config.wmsLayers,
        VERSION: config.wms_version,
        STYLES: styles,
        ...customParams,
      },
      imageLoadFunction: (image, src) => {
        const imageElement = image.getImage() as HTMLImageElement;
        imageElement.src = this._capImageWmsUrl(src);
      },
    };

    const newLayerSource = new ImageWMS(sourceConfig);
    newLayerSource.set('config', config);
    newLayerSource.set('customPrintSerializerParams', {
      ...customParams,
    });
    this._registerImageWmsSourceEvents(newLayerSource);

    const layerConfig = {
      properties: { id: idBod },
      source: newLayerSource,
      opacity: config.opacity || AppConfig.DEFAULT_LAYER_OPACITY,
      visible: false,
      zIndex: AppConfig.ZINDEX_THEMELAYERS,
      maxResolution: config.maxResolution,
      minResolution: config.minResolution,
    };

    return new ImageLayer(layerConfig);
  }

  private _createWFSLayer(idBod: string, config: Partial<LayerConfigInterface>): VectorLayer<VectorSource> {
    const outerThis = this;
    const projCode = outerThis._map?.getView().getProjection().getCode();
    const formatGeoJson = new GeoJSON();

    let urls: { url: string; name: string }[] = [];

    const sourceConfig: VectorSourceOptions = {
      attributions: config.attribution,
      loader:
        config.type === 'api'
          ? undefined
          : function (this: any, extent, resolution, projection) {
              let allFeatures: Feature<Geometry>[] = [];
              urls = [];

              // clear existing features from src
              if (this.getFeatures().length > 0) {
                (this as VectorSource<Feature<Geometry>>).clear();
                // (this as VectorSource<Geometry>).refresh();
              }

              // fetch using new bbox
              config.wmsLayers?.split(',').forEach(layerName => {
                let wfsUrl = config.wmsUrl + '&typeName=' + layerName;

                if (outerThis._map) {
                  if (projCode?.length) {
                    wfsUrl += `&srsname=${projCode}`;
                    const bbox = outerThis._map.getView().calculateExtent(outerThis._map.getSize());
                    if (bbox?.length) {
                      wfsUrl += `&bbox=${bbox.toString()},${projCode}`;
                    }
                  }
                }
                urls.push({ url: wfsUrl, name: layerName });
              });

              urls.forEach(urlObj => {
                outerThis._httpClient
                  .get<GeoJSONFeatureCollection>(urlObj.url)
                  .pipe(
                    tap(response => {
                      // console.log(`response ${urlObj.name}`, response.features.length);
                      const features = formatGeoJson.readFeatures(response) as Feature<Geometry>[];

                      features.forEach(feature => {
                        const id =
                          feature.get('kommunenummer') +
                          '-' +
                          feature.get('vegnummer') +
                          '-' +
                          feature.get('strekningnummer');
                        feature.setProperties({ origin: urlObj.name, id: id, idBod: config.idBod });
                        //feature.setId();
                        // (this as VectorSource<Geometry>).addFeature(feature);
                      });
                      if (features.length > 0) {
                        (this as VectorSource<Feature<Geometry>>).addFeatures(features);
                        if (!allFeatures) {
                          allFeatures = features;
                        } else {
                          allFeatures = allFeatures.concat(features);
                        }
                      }
                      // console.log(`features`, features);
                    }),
                    take(1)
                  )
                  .subscribe();
              });

              if (allFeatures) {
                (this as VectorSource<Feature<Geometry>>).addFeatures(allFeatures);
              }
              (this as VectorSource<Feature<Geometry>>).changed();
            },
      strategy: bbox,
    };

    const newVectorSource = new VectorSource(sourceConfig);
    // set the config as property of the source
    newVectorSource.set('config', config);

    const layerOptions: VectorLayerOptions<Feature, VectorSource> = {
      maxResolution: config.maxResolution,
      minResolution: config.minResolution,
      opacity: config.opacity || 1,
      source: newVectorSource,
      style: LayerStylesConst[idBod] || [],
      visible: true,
      zIndex: AppConfig.ZINDEX_THEMELAYERS + 1,
      properties: {
        altitudeMode: 'clampToGround',
        id: idBod,
        label: config.label,
        timeEnabled: config.timeEnabled,
      },
    };

    return new VectorLayer(layerOptions);
  }

  private _getFailingLayersText() {
    let failingText = '';
    for (const label of this._layersWithErrors) {
      // Add a comma if this is not first layer.
      if (failingText !== '') {
        failingText += ',';
      }
      failingText += ' ' + label;
    }
    return failingText;
  }

  private _getSourceUrl(idBod: string, config: Partial<LayerConfigInterface>): string | undefined {
    if (this._sourceUrlExceptionMap.has(idBod)) {
      if (idBod === AppConfig.IDBOD_NIB_SATELLITE || idBod === AppConfig.IDBOD_NIB_2) {
        return LayerHelper.resolveNibUrl(this._sourceUrlExceptionMap.get(idBod) ?? '');
      }
    }

    if (idBod === AppConfig.IDBOD_FOREST_ROADS_WFS) {
      let forestRoadUrl = config.wmsUrl + '&typeName=skogsbilveg';

      if (this._map) {
        const projection = this._map.getView().getProjection().getCode();
        const bbox = this._map.getView().calculateExtent(this._map.getSize());
        if (projection?.length) {
          forestRoadUrl += `&srsname=${projection}`;
          if (bbox?.length) {
            forestRoadUrl += `&bbox=${bbox.toString()},${projection}`;
          }
        }
      }
      return forestRoadUrl;
    }

    // Crop URL to strip params as some cause problems for OpenLayers
    return config.wmsUrl?.split('?')[0];
  }

  private _removeErrorLayer(id: string) {
    const index = this._layersWithErrors.indexOf(id, 0);
    if (index > -1) {
      this._layersWithErrors.splice(index, 1);
    }
  }
}
